<template>
  <div>
    <div id="list-questions" class="qa-body">
      <div class="body-header">
        <div v-if="((type !== 'in-video') || (type === 'in-video' && filters.search) && questions.length >= 0 && result ) && !['instructor'].includes(nuxtLayout)" class="result">
          <span>{{ questionsCount }} result{{ questionsCount === 1 ? '' : 's' }}{{ filters.search ? ` for '${filters.search}'` : '' }}</span>
        </div>
        <ButtonArrow
          v-if="type === 'in-video'"
          text="Ask a question" type="span"
          class="button-arrow accent"
          @click="ask"
        />
      </div>
      <div
        v-for="(question, questionIndex) in questions"
        :key="questionIndex"
        class="question"
        :class="{ reported: question.reported && $store.getters['auth/perms'].includes('lms.qa-support'), pinned: question.pinned, resolved: question.resolved, first: questionIndex === 0 }"
      >
        <button
          v-if="type !== 'my' && ($store.getters['auth/perms'].includes('lms.qa-support') || $store.getters['auth/user'].role === 'admin' || ['admin'].includes(nuxtLayout))"
          class="pin-question"
          :class="{ selected: question.pinned }"
          @click="pinQuestion(!question.pinned, question.id)"
        >
          <PinIcon />
        </button>
        <AvatarLink v-if="type !== 'in-video'" class="avatar" :info="question" />
        <div class="question-info">
          <div class="question-header">
            <AvatarLink v-if="type === 'in-video'" class="avatar" :info="question" />
            <UserLink :info="question" data-event="question_user.click" />

            <div class="badge">
              <UserBadge v-if="question.userRole === 0 || question.userRole === 2 || (question.userPerms && question.userPerms.includes('lms.qa-support'))" :badge="'Instructor'" />
              <UserBadge v-if="!(question.userRole === 0 || question.userRole === 2 || (question.userPerms && question.userPerms.includes('lms.qa-support'))) && question.userPaid && +question.userLevel >= 8" :badge="'Super learner'" tooltip="This user is a Super Learner. To become a Super Learner, you need to reach Level 8." />
            </div>
            <div class="stats">
              <div class="votes">
                {{ question.votes }} <UpArrowIcon />
              </div>
              <div class="answers">
                {{ question.answers }} <CommentIcon />
              </div>
            </div>
          </div>
          <component
            :is="$config.project !== 'site' && (type === 'in-video' || ['instructor'].includes(nuxtLayout)) ? 'div' : 'nuxt-link'"
            class="question-link"
            :to="$config.project !== 'site' && (type === 'in-video' || ['instructor'].includes(nuxtLayout)) ? '' : `/question/${question.url}/`"
            :data-event="`${type === 'main' && $config.project === 'lms' ? 'question.click' : ''}`"
            v-on="$config.project !== 'site' && (type === 'in-video' || ['instructor'].includes(nuxtLayout)) ? { click: () => goToQuestion(question.url) } : {}"
          >
            <div class="question-body">
              <div class="title">
                <span
                  v-if="
                    question.reported &&
                      $store.getters['auth/perms'].includes('lms.qa-support')
                  "
                  class="reported"
                >Reported:</span>
                <span v-else-if="question.resolved" class="resolved">Resolved:</span>
                <span :inner-html.prop="question.title | highlightKeyword(filters.search)" />
              </div>
              <div class="body">
                <span :inner-html.prop="removeTags(question.body) | highlightKeyword(filters.search)" />
              </div>
            </div>
            <div class="question-footer">
              <div v-if="question.course" class="course" :title="question.course">
                {{ question.course }}
              </div>
              <div class="stats" :class="{ 'resize': $store.state.layout === 'mobile' }">
                <div
                  v-if="question.dateUpdated && question.answers && question.answers > 0"
                  class="time"
                  :title="new Date(question.dateUpdated).toLocaleString()"
                >
                  Last answered:
                  <span class="date">
                    {{ question.dateRelative }}
                  </span>
                </div>
                <div class="views">
                  {{ question.views }} views
                </div>
              </div>
            </div>
          </component>
        </div>
      </div>
    </div>
    <div v-if="!noMore && !noResults && !pagination" class="loading-more">
      loading more...
    </div>
    <div v-else-if="(noResults && type === 'my') || (questionsCount === 1 && type === 'my')" class="no-results">
      <h5>Ask a Question</h5>
      <p v-if="!$store.getters['auth/user'].isBusiness">
        Read our guidelines on how to seek advice from your instructors, build positive interactions with fellow data science students, and have fun while advancing in data science.
      </p>
      <p v-else class="business">
        Post questions, connect with like-minded data science students, and have fun while learning data science.
      </p>
      <ButtonArrow
        v-if="(noResults && type === 'my') || (questionsCount === 1 && type === 'my')" type="nuxt-link" to="/qa-hub/"
        text="Q&A Hub"
      />
    </div>
    <ButtonArrow
      v-if="!noResults && questionsCount !== 1 && type === 'my'" type="nuxt-link" to="/qa-hub/"
      text="Q&A Hub"
    />
    <Pagination
      v-if="pagination && questionsCount && type !== 'my'"
      :page="+page || 1"
      :total="questionsCount"
      :per-page="limit"
      :query-params="queryParams"
      align="center"
      data-event="pagination.click"
    />
  </div>
</template>
<script>
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-param-reassign */
import { uniqBy } from 'lodash-es';
import global from '../mixins/global';
import Pagination from '../components/Pagination';
import UpArrowIcon from '../assets/svg/icon-up-arrow.svg?vue-component';
import CommentIcon from '../assets/svg/icon-comment.svg?vue-component';
import PinIcon from '../assets/svg/icon-pin.svg?vue-component';
import UserLink from './UserLink';
import UserBadge from '../UserBadge';
import AvatarLink from './AvatarLink';
import ButtonArrow from '../components/ButtonArrow';

export default {
  components: {
    Pagination,
    UpArrowIcon,
    CommentIcon,
    PinIcon,
    UserLink,
    UserBadge,
    ButtonArrow,
    AvatarLink,
  },
  filters: {
    highlightKeyword(v, keyword) {
      const regex = new RegExp(keyword, 'i');

      const [m = ''] = v.match(regex) || [];

      if (m !== '') {
        return v.replace(m, `<strong>${m}</strong>`);
      }
      return v;
    },
  },
  mixins: [global],
  props: {
    type: { type: [String], default: '' },
    lesson: { type: [Number], default: 0 },
    course: { type: [Number], default: 0 },
    pageProps: { type: [Array, Object], default: () => {} },
    defaultQuestions: { type: Array, required: false },
  },
  data() {
    return {
      questions: [],
      page: +this.$route.query.page || 1,
      filters: {},
      filtersQuery: '',
      noResults: false,
      noMore: false,
      questionsCount: 0,
      limit: 0,
      pagination: true,
      queryParams: null,
      result: false,
    };
  },
  async fetch() {
    this.filters = {};
    if (!this.filters.order) {
      this.filters.order = 'created';
    }

    if (this.defaultQuestions) {
      this.questions = this.defaultQuestions;
      this.noResults = this.questions.length === 0;
      return;
    }

    if (this.type === 'main') {
      this.filters.type = 'main';
      this.filters.layout = this.nuxtLayout;
    }
    if (this.type === 'my') {
      this.filters.filterBy = ['mine'];
    }
    if (this.type === 'in-video') {
      this.filters = this.$store.state['qa-hub'].filters || this.filters;
      if (!this.filters.searchIn) this.filters.searchIn = 'lesson';
      this.filters.type = 'in-video';
    }
    await this.fetchQuestions(this.page, null, true);
  },
  head() {
    if (this.$config.project !== 'lms') {
      const potentialActions = this.$linkedDataDefault('PotentialActions');
      const collection = (this.questions || []).map((item) => {
        const question = {
          '@type': 'ItemPage',
          name: item.title || item.category,
          description: item.description || item.metaDescription,
        };

        if (item.image) {
          question.image = [`${this.$config.imagesUrl}/${item.image}`];
        }

        return question;
      });

      const websiteDefaults = {
        url: `${this.$config.siteUrl}${this.$route.fullPath}`,
        name: this.pageProps.metaTitle,
        description: this.pageProps.metaDescription,
        datePublished: this.dateIsoString(this.pageProps.created),
        dateModified: this.dateIsoString(this.pageProps.updated),
      };

      const linkedData = this.$linkedDataLoad([
        {
          key: 'WebPage',
          values: {
            ...this.pageProps,
            ...websiteDefaults,
            potentialAction: [
              {
                ...potentialActions.search,
                target: [`${this.$config.siteUrl}${this.$route.path}?search={search_term_string}`],
              },
            ],
          },
        },
        {
          key: 'WebSite',
          values: websiteDefaults,
        },
        {
          key: 'CollectionPage',
          values: {
            mainEntity: {
              '@type': 'ItemList',
              itemListElement: collection,
            },
          },
        },
        {
          key: 'BreadcrumbList',
          values: {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Q&A',
                item: this.pageProps.fullPath,
              },
            ],
          },
        },
      ]);

      const link = [];
      if (this.canonicalURL) {
        link.push({ rel: 'canonical', href: this.canonicalURL() });
      }

      const rels = this.paginationRels({ current: this.page, total: this.questionsCount, perPage: this.limit }) || [];
      link.push(...rels);

      return {
        titleTemplate: `${this.pageProps.metaTitle}%s`,
        link,
        meta: this.metaProps(this.pageProps),
        script: [linkedData],
      };
    }
    return {};
  },
  computed: {
    nuxtLayout() {
      return this.$nuxt && this.$nuxt.layoutName;
    },
  },
  watch: {
    async $route(v, o) {
      const p = +v.query.page || 1;
      this.page = p;
      if (this.pagination) {
        await this.fetchQuestions(p);

        if (document.querySelector('#list-questions') && (v.query.page || o.query.page)) {
          const { top } = document.querySelector('#list-questions').getBoundingClientRect();
          window.scrollTo({ top: window.scrollY + top - 200, behavior: 'smooth' });
        }
      }
    },
    questions(newQuestion, oldQuestion) {
      if (newQuestion !== oldQuestion) {
        this.$emit('questions', { questions: newQuestion, filters: this.filters });
      }
    },
  },
  mounted() {
    if (!this.pagination) {
      this.handleScroll();
    }
  },
  created() {
    if (this.$store.state['qa-hub'].filters?.type && this.$store.state['qa-hub'].filters?.type !== this.type) {
      this.$store.commit('qa-hub/setFilters', null);
    }
    if (this.$store.state['qa-hub'].filters) {
      const currentFilters = this.$store.state['qa-hub'].filters;
      delete currentFilters.search;

      this.$store.commit(
        this.$config.project !== 'site' ? 'qa-hub/setFilters' : 'setFilters',
        currentFilters,
      );
    }

    if (this.$route.query && +this.$route.query.page === 0 && process.client) {
      this.$router.push('');
    }
    this.$root.$off('filtrate');
    this.$root.$emit('new-question-form', false);
    if (process.client && this.type === 'in-video') {
      localStorage.removeItem('currentQuestion');
      this.$root.$on('search-in', async (searchIn) => {
        this.filters.searchIn = searchIn;
        await this.fetchQuestions(this.page);
      });
    }
    this.$root.$on('filtrate', async (queryParams) => {
      this.filters = {};
      const filters = queryParams.split('&');
      const params = filters
        .map((f) => f.split('='))
        .reduce((p, c) => ({ ...p, [c[0]]: c[1] }), {});
      this.queryParams = params;
      this.page = params.page;
      await this.fetchQuestions(this.page, params);
    });
  },
  methods: {
    async pinQuestion(pin, questionId) {
      const action = pin ? 'pin' : 'unpin';
      await this.$axios.$patch(`/qa-hub/questions/${questionId}/${action}`);
      this.page = 1;

      const question = this.questions.find((q) => q.id === questionId);
      if (question) {
        question.pinned = !question.pinned;
      }

      window.scrollTo(0, 0);
      setTimeout(() => {
        this.fetchQuestions(this.page, this.queryParams);
      }, 1000);
    },
    goToQuestion(questionUrl) {
      this.$root.$emit('loadquestion', questionUrl);
    },
    handleScroll() {
      window.onscroll = () => {
        if (
          window.innerHeight + window.scrollY >= document.body.scrollHeight
          && !this.noMore
          && document.getElementById('list-questions')
        ) {
          this.loadMore();
        }
      };
    },
    // TODO: fetchQuestions called two times when instructor access question from the admin panel
    // and then press back button
    async fetchQuestions(page, params, isFetch = false) {
      const filters = params || this.$route.query;
      for (const filter in filters) {
        if (Object.hasOwnProperty.call(filters, filter)) {
          const value = filters[filter];
          this.filters[filter] = value;
        }
      }
      this.filtersQuery = '';
      this.$store.commit(
        this.$config.project !== 'site' ? 'qa-hub/setFilters' : 'setFilters',
        this.filters,
      );

      // eslint-disable-next-line no-restricted-syntax
      for (const fil in this.filters) {
        // eslint-disable-next-line no-prototype-builtins
        if (this.filters.hasOwnProperty(fil)) {
          let filterValue = this.filters[fil] || '';
          if (fil === 'searchIn' && this.type === 'in-video') {
            this.filtersQuery += `&filter[${filterValue}]${
              filterValue === 'course' ? '[]' : ''
            }=${this.$props[filterValue]}`;
          } else if (fil === 'courses' && filterValue.length) {
            filterValue = filterValue.split(',');
            filterValue.forEach((courseId) => {
              this.filtersQuery += `&filter[course][]=${courseId}`;
            });
          } else if (fil === 'filterBy' && filterValue.length) {
            filterValue = filterValue.split(',');
            filterValue.forEach((type) => {
              this.filtersQuery += `&filter[status][]=${type}`;
            });
          } else if (fil === 'order') {
            this.filtersQuery += `&order=${filterValue}`;
          } else if (fil === 'type') {
            this.filtersQuery += `&filter[type]=${filterValue}`;
          } else if (fil === 'search') {
            this.filtersQuery += `&filter[search]=${filterValue}`;
          } else if (fil === 'layout' && filterValue) {
            this.filtersQuery += `&filter[layout]=${filterValue}`;
          }
        }
      }

      const origin = `&origin[site]=${this.$config.project}&origin[type]=${this.type}`;
      this.result = false;
      const query = await this.$axios.$get(
        `/qa-hub/questions?page=${page}${this.filtersQuery}${this.type === 'my' ? '&limit=2' : ''}${origin}`,
      );
      this.result = true;
      this.limit = query.limit;
      this.questionsCount = query.count;
      this.noResults = this.questionsCount === 0;
      if (this.pagination) {
        this.questions = query.questions;
      }
      this.questions = uniqBy(this.questions, 'id');
      let { count } = query;
      let preventHidingQuestions = false;

      // this prevents hiding the questions when the user is an admin or instructor and filter returns 0 questions
      if (['admin', 'instructor'].includes(this.nuxtLayout) && !isFetch && count === 0) {
        count = 1;
        preventHidingQuestions = true;
      } else {
        preventHidingQuestions = false;
      }

      this.$root.$emit('counting', count, preventHidingQuestions, query.unansweredCount, query.unresolvedCount);

    },
    loadMore() {
      this.page += 1;
      this.fetchQuestions(this.page);
    },
    removeTags(html) {
      if (!html) {
        return '';
      }
      html = html.toString();
      return html.replace(/<[^>]*>/g, '');
    },
    ask() {
      if (this.$config.project === 'site') {
        this.$router.push('/signup/');
      } else {
        this.$root.$emit('new-question');
      }
    },
  },
};
</script>
<style scoped lang="scss">
.qa-body {
  .result span {
    font-weight: bold;
  }

  .question {
    padding: var(--spacing-12) var(--spacing-16);
    box-sizing: border-box;
    border-radius: 10px;
    background: var(--main-light);
    border: 1px solid var(--main-light-shade-3);
    border-left-width: 8px;
    position: relative;
    margin: var(--spacing-sm) 0;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
    display: flex;
    align-items: flex-start;

    @include bp($bp-phone) {
      padding: 24px 18px 26px 12px;
    }

    svg {
      vertical-align: middle;

      path[fill] {
        fill: var(--main-light-shade-3) !important;
      }
    }

    .avatar ::v-deep .user-avatar {
      display: block;
    }

    .pin-question {
      height: 25px;
      width: 25px;
      border: 0;
      outline: 0;
      appearance: none;
      background: var(--main-light) !important;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
      border-radius: 50%;
      position: absolute;
      top: -6px;
      left: -16px;

      svg path[stroke] {
        stroke: var(--main-light-shade-4) !important;
      }

      svg path[fill] {
        fill: var(--main-light) !important;
      }

      &:hover {
        cursor: pointer;

        svg path[stroke] {
          stroke: var(--accent) !important;
        }
      }

      &.selected {
        svg path[fill] {
          fill: var(--accent);
        }

        svg path[stroke] {
          stroke: var(--accent);
        }
      }
    }

    .question-info {
      width: 100%;
      min-width: 0;

      .question-header {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .stats {
          display: flex;
          color: var(--main-dark-light-4);
          font-weight: bold;
          flex: 0 0 auto;

          .votes {
            margin-right: var(--spacing-sm);

            svg {
              vertical-align: text-top;
            }
          }
        }
      }

      .question-link {
        text-decoration: none;
        color: unset;
        cursor: pointer;

        .question-body {
          .title {
            font-weight: 700;
            color: var(--main-dark-shade-0);
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;

            span.reported {
              color: var(--main-red-shade-1);
            }

            span.resolved {
              color: var(--main-green-shade-1);
            }

            span {
              ::v-deep {
                strong {
                  color: var(--main-light);
                  background: var(--accent);
                  padding: 0 var(--spacing-4) 0 2px;
                  font-style: italic;
                }
              }
            }
          }

          .body {
            color: var(--main-dark-light-4);
            font-size: 14px;
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;

            span {
              ::v-deep {
                strong {
                  color: var(--main-light);
                  background: var(--accent);
                  padding: 0 var(--spacing-4) 0 2px;
                  font-style: italic;
                }
              }
            }
          }

          @include bp($bp-mobile) {
            .title {
              margin-top: var(--spacing-8);
              margin-bottom: var(--spacing-4);
            }

            .body {
              white-space: normal;
            }
          }
        }

        .question-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 14px;
          margin-top: 15px;
          color: var(--main-dark-light-4);

          .course {
            width: 40%;
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
          }

          .stats {
            margin-left: auto;
            display: flex;
            color: var(--main-light-shade-4);

            .time {
              margin-right: var(--spacing-12);
            }

            @include bp($bp-phone) {
              .time {
                span {
                  display: block;
                }
              }
            }
          }

          @include bp($bp-mobile) {
            flex-direction: column;
            align-items: flex-start;

            .course {
              width: 100%;
              margin-bottom: var(--spacing-16);
            }

            .stats {
              flex-direction: column;
              align-items: flex-start;
              margin: 0;
            }
          }
        }
      }
    }

    &.resolved {
      background: var(--main-green-light-1);
      border-color: var(--main-light-shade-3);
    }

    &.reported {
      border-left: 8px solid var(--main-red-shade-1);
    }

    &.reported,
    &.resolved {
      svg:not(.userrole-svg) path[fill] {
        fill: var(--light-gray);
      }
    }

    &.pinned {
      .pin-question {
        svg path[fill] {
          fill: var(--accent) !important;
        }

        svg path[stroke] {
          stroke: var(--accent) !important;
        }
      }
    }
  }

  @-moz-document url-prefix() {
    .question.question {
      filter: unset;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
  }
}

.no-results {
  text-align: center;
  font-weight: bold;
  margin: var(--spacing-sm);

  h5 {
    color: var(--main-dark-light-4);
    margin: 0;
  }

  p {
    width: 60%;
    margin: 10px auto;
    font-weight: normal;
    color: var(--main-dark-light-4);

    &.business {
      width: 61%;
    }
  }
}

.loading-more {
  color: var(--gray);
  background: var(--lighter-gray);
  border-radius: 8px;
  text-align: center;
  padding: 10px 0;
  width: 100%;
  margin-top: var(--spacing-mdsm);
}

@include bp($bp-mobile) {
  .qa-body {
    .question {
      .pin-question {
        left: -8px;
        top: -8px;
      }

      .question-heading {
        align-items: flex-start;

        .question-titles {
          .title {
            display: block;
            display: -webkit-box;
            line-height: 1.2;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            margin-bottom: 12px;
            white-space: unset;
            width: 100%;
          }

          .question-body {
            width: unset;
            white-space: unset;
            display: block;
            display: -webkit-box;
            line-height: 17px;
            -webkit-line-clamp: 4;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            margin-bottom: 12px;
          }
        }
      }

      .question-info {
        .question-header {
          align-items: flex-end;
        }
      }
    }

    .question-info {
      .question-link {
        .question-body {
          .course {
            width: 90%;
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
          }
        }

        .question-footer {
          .stats {
            .time {
              display: flex;

              .date {
                margin-left: var(--spacing-4);
              }
            }

            &.resize {
              @media only screen and (max-width: 346px) {
                .time {
                  display: block;
                }

                .date {
                  margin-left: 0;
                }
              }
            }
          }
        }
      }
    }
  }

  .no-results {
    text-align: left;
    margin-left: 0;

    p,
    p.business {
      width: 100%;
    }
  }
}

.fa {
  .qa-body .question {
    border-top: none;
    border-right: none;
    border-bottom: none;

    .question-info .question-link .question-body {
      .body {
        font-weight: 500;

        span {
          ::v-deep {
            strong {
              background: var(--accent-light-3-400);
            }
          }
        }
      }

      .title {
        font-weight: 600;
        line-height: 150%;

        span {
          ::v-deep {
            strong {
              background: var(--accent-light-3-400);
            }
          }
        }
      }
    }
  }
}
</style>
